// Client helper: forwards a lead directly to the governed Clinic OS intake
// endpoint on the control-plane Supabase project. No service-role key or
// intake token is used or exposed here — the endpoint accepts anonymous
// browser POSTs and enforces its own auth/rate-limits server-side.

export type ClinicOsLead = {
  name?: string;
  phone?: string;
  whatsapp?: string;
  email?: string;
  language?: string;
  clinic_id?: string | null;
  message?: string;
  notes?: string;
  treatment?: string;
  treatment_interest?: string;
  procedure_interest?: string;
  source?: string;
  // Appointment / calendar hints — Clinic OS creates a Calendar row when a
  // preferred_date is present. Send YYYY-MM-DD.
  preferred_date?: string | null;
  booking_date?: string | null;
  requested_date?: string | null;
  preferred_time?: string | null;
  source_page?: string;
  turnstile_token?: string;
};

export interface LeadTriage {
  detected_language: string;
  source_page: string;
  treatment_interest: string;
  country_or_location: string;
  urgency_level: "low" | "medium" | "high" | "urgent";
  lead_quality_score: number;
  preferred_contact_channel: "whatsapp" | "phone" | "email";
  recommended_next_action: string;
  safe_whatsapp_reply_draft: string;
  compliance_flags: string[];
  classifier: "ai" | "deterministic";
  model_version?: string;
}

export interface AgentQueueSummary {
  producer?: string;
  status?: string;
  queued_agents?: string[];
  skipped_agents?: string[];
  errors?: unknown[];
}

export type ClinicOsResult =
  | {
      ok: true;
      duplicate?: boolean;
      id?: string;
      triage?: LeadTriage;
      upstream_status?: number;
      agent_queue?: AgentQueueSummary;
    }
  | { ok: false; error: string; message?: string };

function normalizeErrorMessage(message: string): string {
  const normalized = message.toLowerCase();
  if (normalized.includes("function") && normalized.includes("not found")) return "function_not_found";
  if (normalized.includes("404")) return "function_not_found";
  if (normalized.includes("failed to fetch") || normalized.includes("network")) return "network_error";
  if (normalized.includes("timeout") || normalized.includes("aborted")) return "network_error";
  return "unknown";
}

const CLINIC_OS_INTAKE_URL = `${
  (import.meta.env.VITE_CLINIC_OS_SUPABASE_URL as string | undefined) ||
  "https://npqrumbxltrcnscumeyx.supabase.co"
}/functions/v1/clinic-os-lead-intake`;

async function safeParseJson(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { raw: text };
  }
}

export async function forwardLeadToClinicOs(lead: ClinicOsLead): Promise<ClinicOsResult> {
  try {
    const page_url = typeof window !== "undefined" ? window.location.href : undefined;
    const response = await fetch(CLINIC_OS_INTAKE_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...lead, page_url }),
    });

    const data = (await safeParseJson(response)) as Record<string, unknown> | null;

    if (!response.ok) {
      const message =
        (data && typeof data.message === "string" && data.message) ||
        (data && typeof (data as { error?: unknown }).error === "string" && (data as { error: string }).error) ||
        `HTTP ${response.status}`;
      console.error("clinic-os forward error:", message);
      return { ok: false, error: normalizeErrorMessage(message), message };
    }

    if (!data || typeof (data as { ok?: unknown }).ok !== "boolean") {
      return { ok: false, error: "unknown" };
    }

    return data as unknown as ClinicOsResult;
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    console.error("clinic-os forward exception:", message);
    return { ok: false, error: normalizeErrorMessage(message), message };
  }
}

export function clinicOsDuplicateMessage(language: string): string {
  const map: Record<string, string> = {
    en: "Thank you, we already received your request and our team will contact you.",
    nl: "Bedankt, we hebben uw aanvraag al ontvangen en ons team neemt contact met u op.",
    fr: "Merci, nous avons déjà reçu votre demande et notre équipe vous contactera.",
    tr: "Teşekkürler, talebinizi zaten aldık ve ekibimiz sizinle iletişime geçecek.",
    ar: "شكراً لك، لقد تلقينا طلبك بالفعل وسيتواصل معك فريقنا.",
  };
  return map[language] || map.en;
}
